import React from "react";
import { X, CheckCircle2, TrendingUp, Sparkles, Zap } from "lucide-react";

/**
 * SampleAssessmentModal - anonymised, redacted 7-part Hiring Assessment shown
 * inside the upgrade flow as social proof. Matches the exact structure of
 * `ReportPanel` in VideoMockInterview.jsx (overall score, readiness score,
 * competency breakdown, strengths, improvements, coaching, next steps,
 * hiring recommendation).
 *
 * All scores and text are illustrative - no real candidate data.
 */

const SAMPLE = {
  role: "Senior Product Manager",
  company: "Anonymised · Series B SaaS",
  overall_score: 82,
  readiness_score: 79,
  level: "Strong",
  speech_pace_wpm: 138,
  filler_words_count: 11,
  communication: 84,
  confidence: 78,
  answer_structure: 80,
  job_fit: 85,
  leadership_presence: 75,
  executive_presence: 77,
  star_score: 81,
  hiring_recommendation: "ready",
  strengths: [
    "Clear articulation of product strategy with quantified customer outcomes",
    "Strong storytelling around cross-functional influence at Series B stage",
    "Well-structured STAR answers with measurable business impact",
  ],
  improvements: [
    "Tighten opening 30 seconds, context ran long on 2 of 5 answers",
    "Add explicit stakeholder trade-off reasoning when discussing prioritisation",
    "Reduce hedging language (\"kind of\", \"I guess\") during technical depth questions",
  ],
  coaching:
    "Your judgement and product intuition already read at Senior PM level, the hiring manager will lean in when you talk about the mobile checkout launch. Your weak spot is scoping: you dive into details before framing the problem. Practise a two-sentence problem framing before every answer and you'll close the gap to Staff PM within one cycle.",
  next_steps: [
    "Rehearse 2 more mocks focused on stakeholder trade-off framing",
    "Prepare 3 quantified stories that map to leadership principles",
    "Record and review one 60-second self-intro until it lands in under 45s",
  ],
};

const RECOMMENDATION_LABEL = {
  ready: { label: "Interview Ready", dot: "●", classes: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  needs_practice: { label: "Needs More Practice", dot: "●", classes: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
  not_ready: { label: "Not Interview Ready", dot: "●", classes: "text-rose-300 border-rose-500/30 bg-rose-500/10" },
};

function ScoreBar({ label, value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const col = v >= 80 ? "bg-emerald-400" : v >= 60 ? "bg-amber-400" : "bg-rose-400";
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-zinc-400">{label}</span>
        <span className="text-zinc-100 font-semibold tabular-nums">{v}</span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
        <div className={`h-full ${col} rounded-full transition-all`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
}

export default function SampleAssessmentModal({ open, onClose, onUnlockClick }) {
  if (!open) return null;
  const rec = RECOMMENDATION_LABEL[SAMPLE.hiring_recommendation];

  return (
    <div
      data-testid="sample-assessment-modal"
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 shadow-[0_0_80px_-20px_rgba(245,158,11,0.35)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-6 py-4 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" /> Sample · Hiring Assessment
            </div>
            <div className="text-sm font-semibold text-zinc-100 mt-0.5">
              {SAMPLE.role} <span className="text-zinc-500">· {SAMPLE.company}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="sample-assessment-close"
            className="text-zinc-500 hover:text-zinc-100 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Redaction disclaimer */}
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 border-l-2 border-amber-500/40 pl-3">
            Anonymised sample from a real candidate flow · No PII shown
          </div>

          {/* Recommendation pill + big scores */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-400 mb-1">
                Executive Interview Report
              </div>
              <h3 className="text-xl font-bold text-zinc-50 tracking-tight">
                Overall verdict: the honest coaching call
              </h3>
            </div>
            <div
              className={`inline-flex items-center gap-2 border rounded-full px-4 py-1.5 text-xs font-semibold ${rec.classes}`}
            >
              <span className="text-sm leading-none">{rec.dot}</span> {rec.label}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] via-zinc-900/40 to-zinc-950/60 p-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-400">Overall Interview Score</div>
              <div className="mt-1 text-3xl font-bold text-zinc-50">
                {SAMPLE.overall_score}
                <span className="text-base text-zinc-500">/100</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-amber-400/80 mt-1">
                {SAMPLE.level}
              </div>
            </div>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Official Readiness Score</div>
              <div className="mt-1 text-3xl font-bold text-zinc-50">
                {SAMPLE.readiness_score}
                <span className="text-base text-zinc-500">/100</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">Post-interview</div>
            </div>
            <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Speech Pace</div>
              <div className="mt-1 text-3xl font-bold text-zinc-50">
                {SAMPLE.speech_pace_wpm}
                <span className="text-base text-zinc-500"> wpm</span>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                {SAMPLE.filler_words_count} filler words
              </div>
            </div>
          </div>

          {/* Competency breakdown */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-sm font-semibold text-zinc-50 mb-3">Competency Breakdown</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
              <ScoreBar label="Communication" value={SAMPLE.communication} />
              <ScoreBar label="Confidence" value={SAMPLE.confidence} />
              <ScoreBar label="Answer Structure" value={SAMPLE.answer_structure} />
              <ScoreBar label="Job Fit" value={SAMPLE.job_fit} />
              <ScoreBar label="Leadership Presence" value={SAMPLE.leadership_presence} />
              <ScoreBar label="Executive Presence" value={SAMPLE.executive_presence} />
              <ScoreBar label="STAR Framework" value={SAMPLE.star_score} />
            </div>
          </div>

          {/* Strengths / Improvements */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.03] p-4">
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Strengths</div>
              <ul className="space-y-1.5 text-xs text-zinc-200">
                {SAMPLE.strengths.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-amber-500/25 bg-amber-500/[0.03] p-4">
              <div className="text-[10px] uppercase tracking-widest text-amber-400 mb-2">Improvement Areas</div>
              <ul className="space-y-1.5 text-xs text-zinc-200">
                {SAMPLE.improvements.map((s, i) => (
                  <li key={i} className="flex gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Coaching */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Personalized Coaching</div>
            <p className="text-sm text-zinc-200 leading-relaxed">{SAMPLE.coaching}</p>
          </div>

          {/* Next steps */}
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Next Steps</div>
            <ul className="space-y-1.5 text-xs text-zinc-200">
              {SAMPLE.next_steps.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-amber-400 mt-0.5">→</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Sticky footer with unlock CTA */}
        <div className="sticky bottom-0 border-t border-zinc-800 bg-zinc-950/95 backdrop-blur px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-[11px] text-zinc-500 leading-relaxed">
            This is a redacted sample. Your Assessment will be uniquely yours: role, JD, and voice-analysed answers.
          </div>
          <button
            onClick={onUnlockClick}
            data-testid="sample-assessment-unlock"
            className="inline-flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-400 text-amber-950 font-semibold rounded-lg px-4 py-2 shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)] flex-shrink-0"
          >
            <Zap className="h-3.5 w-3.5" />
            Unlock my Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
